import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronRightIcon,
  ClockIcon,
  XMarkIcon,
  CalendarDaysIcon,
  CurrencyRupeeIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

const InternshipCard = ({ data, onJoin, processingId, disabled }) => {
  const [showDescModal, setShowDescModal] = useState(false);
  const isProcessing = processingId === data.id;

  // ── Expired check ────────────────────────────────────────────────────────────
  const isExpired =
    disabled || (data.end_date && new Date(data.end_date) < new Date());

  // ── Formatters ───────────────────────────────────────────────────────────────
  const renderIncentive = (val) => {
    if (!val || val === "0" || val === 0) return "Certification";
    const numericVal = Number(val);
    if (!isNaN(numericVal)) return `₹${numericVal.toLocaleString('en-IN')}`;
    return String(val);
  };

  const formatCurrency = (val) => {
    if (!val || val === "0" || val === 0) return "Free";
    const numericVal = Number(val);
    return isNaN(numericVal) ? val : `₹${numericVal.toLocaleString('en-IN')}`;
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return "Dates to be announced";
    const s = new Date(start);
    const e = new Date(end);
    const sameMonth =
      s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
    const sameYear = s.getFullYear() === e.getFullYear();
    if (sameMonth) {
      return `${s.getDate()}–${e.getDate()} ${s.toLocaleDateString('en-GB', { month: 'short' })}`;
    }
    if (sameYear) {
      return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
    }
    return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} – ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  const description = data.description || "Structured industry immersion program.";
  const isLongDesc = description.length > 100;
  const previewDesc = isLongDesc
    ? description.slice(0, 100).trimEnd() + '...'
    : description;

  return (
    <>
      {/* ── CARD ─────────────────────────────────────────────────────────────── */}
      <div
        className={`flex flex-col bg-[#0b0b0b] border border-white/10 rounded-2xl overflow-hidden transition-colors duration-300 shadow-lg ${
          isExpired ? 'opacity-70' : 'hover:border-emerald-500/40'
        }`}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex justify-between items-start">
          <span
            className={`flex items-center gap-2 text-xs px-3 py-1 rounded-full border font-medium ${
              isExpired
                ? 'text-gray-500 border-gray-800 bg-gray-900'
                : 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
            }`}
          >
            <ClockIcon className="h-4 w-4" />
            {data.duration_months || 3} Month Program
          </span>
          <span className="text-xs text-gray-500">
            {formatDateRange(data.start_date, data.end_date)}
          </span>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 flex-grow">
          <h3
            className={`text-lg font-semibold mb-3 leading-snug ${
              isExpired ? 'text-gray-500' : 'text-white'
            }`}
          >
            {data.title}
          </h3>

          {/* ✅ Fixed 2-line preview — never expands inline */}
          <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
            {previewDesc}
          </p>

          {/* Read more opens modal */}
          {isLongDesc && (
            <button
              onClick={() => setShowDescModal(true)}
              className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 transition-colors duration-200 underline underline-offset-2"
            >
              Read more
            </button>
          )}
        </div>

        {/* Info Grid */}
        <div className="px-6 py-4 border-t border-white/5 bg-white/[0.02] grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-gray-500 mb-1 text-xs uppercase tracking-wide">
              Security Deposit
            </div>
            <div className="text-gray-300 font-medium">
              {formatCurrency(data.entry_fee || data.fee)}
            </div>
          </div>
          <div>
            <div className="text-gray-500 mb-1 text-xs uppercase tracking-wide">
              Stipend / Incentive
            </div>
            <div
              className={`font-medium text-sm leading-snug ${
                isExpired ? 'text-gray-600' : 'text-emerald-400'
              }`}
            >
              {renderIncentive(data.prize_pool || data.reward || data.stipend)}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <button
          disabled={isProcessing || isExpired}
          onClick={() => onJoin(data)}
          className={`w-full py-4 text-sm font-medium transition-colors duration-300 flex items-center justify-center gap-2 ${
            isExpired
              ? 'bg-gray-900 text-gray-600 cursor-not-allowed'
              : isProcessing
              ? 'bg-emerald-700 text-white cursor-wait'
              : 'bg-emerald-600 text-white hover:bg-emerald-500'
          }`}
        >
          {isProcessing ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Processing...
            </>
          ) : isExpired ? (
            'Batch Closed'
          ) : (
            <>
              Enroll <ChevronRightIcon className="h-4 w-4" />
            </>
          )}
        </button>
      </div>

      {/* ── DESCRIPTION MODAL (portal) ───────────────────────────────────────── */}
      {showDescModal &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setShowDescModal(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

            {/* Modal panel */}
            <div
              className="relative z-10 w-full sm:max-w-lg bg-[#0f0f0f] border border-white/10 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-white/5">
                <div className="flex-1 pr-4">
                  {/* Duration badge */}
                  <span className="flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-medium mb-2 w-fit">
                    <ClockIcon className="h-3 w-3" />
                    {data.duration_months || 3} Month Program
                  </span>
                  <h2 className="text-base font-bold text-white leading-snug mt-1">
                    {data.title}
                  </h2>
                </div>
                {/* Close button */}
                <button
                  onClick={() => setShowDescModal(false)}
                  className="shrink-0 p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Modal body — scrollable */}
              <div className="px-6 py-5 max-h-[50vh] overflow-y-auto">
                <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line">
                  {description}
                </p>
              </div>

              {/* Modal footer — key details including duration (internship-specific) */}
              <div className="px-6 py-4 border-t border-white/5 bg-white/[0.02] grid grid-cols-3 gap-4">
                {/* Duration — internship specific field */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1 text-gray-500">
                    <ClockIcon className="h-3 w-3" />
                    <span className="text-[10px] uppercase tracking-wide">Duration</span>
                  </div>
                  <span className="text-xs text-gray-300 font-medium">
                    {data.duration_months || 3} Months
                  </span>
                </div>

                {/* Deposit */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1 text-gray-500">
                    <CurrencyRupeeIcon className="h-3 w-3" />
                    <span className="text-[10px] uppercase tracking-wide">Deposit</span>
                  </div>
                  <span className="text-xs text-gray-300 font-medium">
                    {formatCurrency(data.entry_fee || data.fee)}
                  </span>
                </div>

                {/* Stipend */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1 text-gray-500">
                    <TrophyIcon className="h-3 w-3" />
                    <span className="text-[10px] uppercase tracking-wide">Stipend</span>
                  </div>
                  <span className="text-xs text-emerald-400 font-medium leading-tight">
                    {renderIncentive(data.prize_pool || data.reward || data.stipend)}
                  </span>
                </div>
              </div>

              {/* Modal CTA */}
              <div className="px-6 pb-6 pt-4">
                <button
                  disabled={isProcessing || isExpired}
                  onClick={() => {
                    setShowDescModal(false);
                    onJoin(data);
                  }}
                  className={`w-full py-3.5 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                    isExpired
                      ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                      : isProcessing
                      ? 'bg-emerald-700 text-white cursor-wait'
                      : 'bg-emerald-600 text-white hover:bg-emerald-500'
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : isExpired ? (
                    'Batch Closed'
                  ) : (
                    <>
                      Enroll Now <ChevronRightIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default InternshipCard;